import React, { useState, useMemo } from 'react';
import { formatDate } from '../../../utils/format.utils';
import CapsuleDetailsTable from './CapsuleDetailsTable';

const getUsedDilutions = (content) => {
    if (!content || !Array.isArray(content)) return [];
    
    const used = content.reduce((acc, item) => {
        if (item.dilution && !acc.includes(item.dilution.label)) {
            acc.push(item.dilution.label);
        }
        if (!item.dilution && item.mixture?.defaultDilution?.label) {
            const dilution = item.mixture.defaultDilution.label;
            if (!acc.includes(dilution)) {
                acc.push(dilution);
            }
        }
        return acc;
    }, []);
    
    return used;
};

const CapsuleDetails = ({ capsule }) => {
    const configurations = capsule.configurations || [];
    const [selectedVersion, setSelectedVersion] = useState(configurations.length > 0 ? configurations.length - 1 : 0);
    const [configuration, setConfiguration] = useState(configurations[0]?.configuration || null);

    // Find the initial configuration (the one with the smallest ID)
    const initialConfig = useMemo(() => {
        if (!configurations || configurations.length === 0) return null;
        return configurations
            .sort((a, b) => new Date(b?.creationDate) - new Date(a?.creationDate))
            .reduce((initial, current) => {
                return current.id < initial.id ? current : initial;
            }, configurations[0]);
    }, [configurations]);

    // Find the index of the initial configuration in the array
    const initialConfigIndex = useMemo(() => {
        if (!initialConfig) return -1;
        return configurations
            .sort((a, b) => new Date(b?.creationDate) - new Date(a?.creationDate))
            .findIndex(config => config.id === initialConfig.id);
    }, [configurations, initialConfig]);

    const [initialConfiguration, setInitialConfiguration] = useState(initialConfig?.configuration || null);

    // Determine if validation column should be shown
    const showValidationColumn = useMemo(() => {
        return selectedVersion !== initialConfigIndex;
    }, [selectedVersion, initialConfigIndex]);

    // Get dilutions used in the current configuration
    const usedDilutions = useMemo(() => {
        if (!configurations.length || !configurations[selectedVersion]?.configuration?.content) {
            return [];
        }
        return getUsedDilutions(configurations[selectedVersion].configuration.content);
    }, [configurations, selectedVersion]);

    // Check if the capsule is valid
    const isValid = useMemo(() => {
        return capsule.configurations && capsule.configurations.some(config => config.isValid);
    }, [capsule.configurations]);

    const handleVersionChange = (e) => {
        const index = parseInt(e.target.value);
        setSelectedVersion(index);
        setConfiguration(configurations[index]?.configuration || null);
    };

    if (!capsule) {
        return <div className="alert alert-warning">Aucune information de capsule disponible</div>;
    }

    return (
        <div className="bg-base-100 rounded-lg p-4 space-y-4">
            <div className="flex flex-col md:flex-row justify-between gap-4">
                <div className="flex flex-col gap-2">
                    <h3 className="text-lg font-semibold">{capsule.name}</h3>
                    <p className="text-sm text-gray-500">{capsule.information || 'Aucune information'}</p>
                </div>
                
                <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Statut:</span>
                        <span className={`badge ${isValid ? 'badge-primary' : 'badge-error'} shadow-sm`}>
                            {isValid ? 'Validée' : 'Non validée'}
                        </span>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                        <span className="text-sm font-medium">Dilutions:</span>
                        {usedDilutions.map((dilution, index) => (
                            <span key={index} className="badge badge-outline badge-sm">
                                {dilution}
                            </span>
                        ))}
                        {usedDilutions.length === 0 && (
                            <span className="text-sm text-gray-500">Aucune dilution</span>
                        )}
                    </div>
                </div>
            </div>

            {configurations && configurations.length > 0 ? (
                <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-2">
                        <label className="text-sm font-medium">Configuration:</label>
                        <select
                            className="select select-bordered select-sm"
                            value={selectedVersion}
                            onChange={handleVersionChange}
                        >
                            {configurations
                                .sort((a, b) => new Date(b?.creationDate) - new Date(a?.creationDate))
                                .map((config, index) => (
                                    <option key={config.id} value={index}>
                                        {formatDate(config.creationDate, false)}
                                        {config.id === initialConfig?.id ? " (Initiale)" : ""}
                                    </option>
                                ))}
                        </select>
                    </div>

                    {configuration && (
                        <div className="flex flex-wrap gap-4 text-sm">
                            <span className="badge badge-outline badge-primary">
                                Physique: <strong>{configuration.totalPhysique + configuration.totalOther}</strong>
                            </span>
                            <span className="badge badge-outline badge-accent">
                                Effluences: <strong>{configuration.totalEffluence}</strong>
                            </span>
                        </div>
                    )}

                    {configuration ? (
                        <CapsuleDetailsTable
                            configuration={configuration}
                            initialConfiguration={initialConfiguration}
                            showValidationColumn={showValidationColumn}
                        />
                    ) : (
                        <div className="alert alert-warning shadow-sm">
                            <p>Aucune configuration disponible</p>
                        </div>
                    )}
                </div>
            ) : (
                <div className="alert alert-warning shadow-sm">
                    <p>Aucune configuration pour cette capsule</p>
                </div>
            )}
        </div>
    );
};

export default CapsuleDetails; 